import { DataDrivenPropertyValueSpecification, EasingOptions, LngLatBoundsLike } from "mapbox-gl";
import { FC, JSX, useCallback, useContext, useEffect, useRef, useState } from "react";
import * as ReactMapGL from "react-map-gl/mapbox";
import { Layer, NavigationControl, Source, type MapRef } from "react-map-gl/mapbox";
import { MappingContext } from "@app/mapping/MappingContext";
import ProviderFootprintPopup from "@app/overviews/market-expansion-dashboard/components/provider-footprint-map/ProviderFootprintPopup";
import { ContractEntityColorTheme } from "@app/overviews/market-expansion-dashboard/contexts/ContractEntityColorThemeContext";
import { MarketExpansionFiltersContext } from "@app/overviews/market-expansion-dashboard/contexts/MarketExpansionFiltersContext";
import { MarketExpansionMapDataContext } from "@app/overviews/market-expansion-dashboard/contexts/MarketExpansionMapDataContext";
import { ProviderFootprintMapDataContext } from "@app/overviews/market-expansion-dashboard/contexts/ProviderFootprintMapDataContext";
import {
  COUNTY_OUTLINE_COLORS,
  EMPTY_FEATURE_COLLECTION,
  HOVERED_COUNTY_BORDER_LAYER_ID,
  HOVERED_COUNTY_SOURCE_ID,
  MAP_BOUNDS_COUNTY_PADDING,
  MAP_BOUNDS_PADDING,
  MAP_BOUNDS_PROVIDER_CLUSTER_PADDING,
  MAP_DEFAULT_ZOOM,
  MAP_PAN_DURATION,
  PROVIDER_FOOTPRINT_CLICK_PIXEL_RADIUS,
  PROVIDER_FOOTPRINT_COUNTY_BORDER_LAYER_ID,
  PROVIDER_FOOTPRINT_COUNTY_SOURCE_ID,
  PROVIDER_FOOTPRINT_MAX_ZOOM,
  PROVIDER_FOOTPRINT_PROVIDER_LAYER_ID,
  PROVIDER_FOOTPRINT_PROVIDER_SOURCE_ID,
  PROVIDER_FOOTPRINT_SELECTED_CE_LAYER_ID,
  PROVIDER_FOOTPRINT_SELECTED_CE_SOURCE_ID,
  SELECTED_COUNTY_BORDER_LAYER_ID,
  SELECTED_COUNTY_SOURCE_ID,
} from "@app/overviews/market-expansion-dashboard/MarketExpansionDashboardConstants";
import {
  CONTRACT_ENTITY_1_COLOR_SLOT_KEY,
  CONTRACT_ENTITY_2_COLOR_SLOT_KEY,
  CONTRACT_ENTITY_3_COLOR_SLOT_KEY,
  CONTRACT_ENTITY_4_COLOR_SLOT_KEY,
  CONTRACT_ENTITY_5_COLOR_SLOT_KEY,
  CONTRACT_ENTITY_REST_COLOR_SLOT_KEY,
} from "@app/overviews/market-expansion-dashboard/shared/ContractEntityColorThemeConstants";
import {
  adjustColorBrightness,
  getFeatureClusterBounds,
} from "@app/overviews/market-expansion-dashboard/shared/marketExpansionMapHelper";
import { ProviderFootprintFeatureProperties } from "@app/overviews/market-expansion-dashboard/shared/ProviderFootprintFeatureProperties";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { MapAppearanceControl } from "@app/shared/map-appearance/components/MapAppearanceControl";
import { MapStyle } from "@app/shared/map-appearance/interfaces";
import { MapboxSupportAlert } from "@app/shared/mapping/MapboxSupportAlert";
import { RecenterControl } from "@app/shared/mapping/RecenterControl";

const WrappedProviderFootprintMap: FC = (): JSX.Element => {
  const mappingContext = useContext(MappingContext);
  const filtersContext = useContext(MarketExpansionFiltersContext);
  const mapDataContext = useContext(MarketExpansionMapDataContext);
  const providerFootprintContext = useContext(ProviderFootprintMapDataContext);
  const mapRef = useRef<MapRef>(null);

  const { getColor } = ContractEntityColorTheme.useMapColorTheme();

  const imagesDir = `${window.customUIBaseUrl}/images/`;

  const [viewState, setViewState] = useState<ReactMapGL.ViewState>({ ...mappingContext.defaultViewState, zoom: MAP_DEFAULT_ZOOM });
  const [mapStyle, setMapStyle] = useState<string>(mappingContext.qesLightStyle);
  const [hoverPosition, setHoverPosition] = useState<ReactMapGL.LngLat | null>(null);
  const [hoveredProviders, setHoveredProviders] = useState<ProviderFootprintFeatureProperties[]>([]);
  const [selectedProviders, setSelectedProviders] = useState<ProviderFootprintFeatureProperties[]>([]);
  const [manualMapControl, setManualMapControl] = useState<boolean>(false);

  /** Get features within a pixel radius of the mouse, ordered by features under mouse first (ccnTotalCases descending),
   * and then the nearby features (ccnTotalCases descending). This sorting makes popup behavior more natural for user. */
  const getOrderedFeaturesInRadius = (e: ReactMapGL.MapMouseEvent): ReactMapGL.MapGeoJSONFeature[] => {
    const r = PROVIDER_FOOTPRINT_CLICK_PIXEL_RADIUS;
    const bbox: [[number, number], [number, number]] = [
      [e.point.x - r, e.point.y - r],
      [e.point.x + r, e.point.y + r],
    ];
    const featuresInRadius =
      mapRef.current?.queryRenderedFeatures(bbox, { layers: [PROVIDER_FOOTPRINT_PROVIDER_LAYER_ID, PROVIDER_FOOTPRINT_SELECTED_CE_LAYER_ID] }) ?? [];

    const featuresUnderMouse = e.features ?? [];
    const ccnsUnderMouse = new Set(featuresUnderMouse.map((f) => (f.properties as ProviderFootprintFeatureProperties).ccn));
    const featuresNearMouse = featuresInRadius.filter((f) => !ccnsUnderMouse.has((f.properties as ProviderFootprintFeatureProperties).ccn));

    const byDescCases = (a: ReactMapGL.MapGeoJSONFeature, b: ReactMapGL.MapGeoJSONFeature) =>
      (b.properties as ProviderFootprintFeatureProperties).serviceAreaCases - (a.properties as ProviderFootprintFeatureProperties).serviceAreaCases;
    return [...featuresUnderMouse.sort(byDescCases), ...featuresNearMouse.sort(byDescCases)];
  };

  const handleHoverOverProviders = (e: ReactMapGL.MapMouseEvent) => {
    const providers = getOrderedFeaturesInRadius(e).map((f) => f.properties as ProviderFootprintFeatureProperties);
    setHoveredProviders(providers);
    setHoverPosition(e.lngLat);
    if (mapRef.current) {
      mapRef.current.getCanvas().style.cursor = "pointer";
    }
  };

  const handleMouseLeave = () => {
    setHoveredProviders([]);
    setHoverPosition(null);
    if (mapRef.current) {
      mapRef.current.getCanvas().style.cursor = "";
    }
  };

  const handleClickOnProvider = (e: ReactMapGL.MapMouseEvent) => {
    const features = getOrderedFeaturesInRadius(e);
    if (features.length) {
      const providers = features.map((f) => f.properties as ProviderFootprintFeatureProperties);
      setSelectedProviders(providers);

      setManualMapControl(true);
      providerFootprintContext.setSelectedContractEntity(null);

      const boundingBox = getFeatureClusterBounds(features);
      if (boundingBox) {
        centerOnBoundingBox(boundingBox, MAP_BOUNDS_PROVIDER_CLUSTER_PADDING);
      }

      if (mapRef.current) {
        mapRef.current.getCanvas().style.cursor = "pointer";
      }
    }
  };

  const onMapLoad = useCallback((): void => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    mapRef.current?.setCenter([mappingContext.defaultLongitude, mappingContext.defaultLatitude]);

    mapRef.current?.on("mousemove", PROVIDER_FOOTPRINT_PROVIDER_LAYER_ID, handleHoverOverProviders);
    mapRef.current?.on("mouseleave", PROVIDER_FOOTPRINT_PROVIDER_LAYER_ID, handleMouseLeave);
    mapRef.current?.on("click", PROVIDER_FOOTPRINT_PROVIDER_LAYER_ID, handleClickOnProvider);
    mapRef.current?.on("mousemove", PROVIDER_FOOTPRINT_SELECTED_CE_LAYER_ID, handleHoverOverProviders);
    mapRef.current?.on("mouseleave", PROVIDER_FOOTPRINT_SELECTED_CE_LAYER_ID, handleMouseLeave);
    mapRef.current?.on("click", PROVIDER_FOOTPRINT_SELECTED_CE_LAYER_ID, handleClickOnProvider);
  }, []);

  useEffect(() => {
    // Remove listeners on component unmount
    return () => {
      mapRef.current?.off("mousemove", PROVIDER_FOOTPRINT_PROVIDER_LAYER_ID, handleHoverOverProviders);
      mapRef.current?.off("mouseleave", PROVIDER_FOOTPRINT_PROVIDER_LAYER_ID, handleMouseLeave);
      mapRef.current?.off("click", PROVIDER_FOOTPRINT_PROVIDER_LAYER_ID, handleClickOnProvider);
      mapRef.current?.off("mousemove", PROVIDER_FOOTPRINT_SELECTED_CE_LAYER_ID, handleHoverOverProviders);
      mapRef.current?.off("mouseleave", PROVIDER_FOOTPRINT_SELECTED_CE_LAYER_ID, handleMouseLeave);
      mapRef.current?.off("click", PROVIDER_FOOTPRINT_SELECTED_CE_LAYER_ID, handleClickOnProvider);
    };
  }, []);

  const onMapMove = useCallback((viewState: ReactMapGL.ViewState): void => {
    setViewState({
      ...viewState,
      // Disables the map rotation changes (keyboard, mouse, touch)
      bearing: mappingContext.defaultBearing,
      // Disables the map pitch changes (keyboard, mouse, touch)
      pitch: mappingContext.defaultPitch,
    });
  }, []);

  const centerOnBoundingBox = (boundingBox: LngLatBoundsLike, padding: number) => {
    const easingOptions: EasingOptions = {
      duration: MAP_PAN_DURATION,
      maxZoom: PROVIDER_FOOTPRINT_MAX_ZOOM,
      padding,
      retainPadding: false,
    };
    mapRef.current?.fitBounds(boundingBox, easingOptions);
  };

  const recenterMap = () => {
    if (filtersContext.selectedState && mapDataContext.dataBoundingBox) {
      // Refocus/zoom to state or county
      const padding = mapDataContext.selectedCountySSA ? MAP_BOUNDS_COUNTY_PADDING : MAP_BOUNDS_PADDING;
      centerOnBoundingBox(mapDataContext.dataBoundingBox, padding);
    } else {
      // Refocus/zoom out to USA
      setViewState({ ...mappingContext.defaultViewState, zoom: MAP_DEFAULT_ZOOM });
      mapDataContext.setMinZoomLevel(mappingContext.defaultZoom - 0.5);
    }
  };

  useEffect(() => {
    // Refocus when user selects/deselects a county; only if map is fully loaded
    if (providerFootprintContext.providerFootprintData !== undefined && !manualMapControl) {
      recenterMap();
    }
  }, [mapDataContext.dataBoundingBox]);

  /** When a Contract Entity is selected, zoom the map and open a popup for its providers */
  useEffect(() => {
    if (filtersContext.selectedState && providerFootprintContext.selectedCEFootprintGeoJson.features.length > 0) {
      const features = providerFootprintContext.selectedCEFootprintGeoJson.features;

      const providers = features.map((f) => f.properties).sort((a, b) => b.serviceAreaCases - a.serviceAreaCases);
      setSelectedProviders(providers);

      setManualMapControl(false);

      const boundingBox = getFeatureClusterBounds(features);
      if (boundingBox) {
        centerOnBoundingBox(boundingBox, MAP_BOUNDS_PROVIDER_CLUSTER_PADDING);
      }
    } else if (!manualMapControl) {
      // manualMapControl is only true after user clicks a provider feature. So if it is still
      // false, then the selectedProviders are still the CE's and need to be cleared.
      setSelectedProviders([]);
      recenterMap();
    }
  }, [providerFootprintContext.selectedCEFootprintGeoJson]);

  useEffect(() => {
    setSelectedProviders([]);
    setManualMapControl(false);
    if (providerFootprintContext.providerFootprintData !== undefined) {
      recenterMap();
    }
  }, [providerFootprintContext.providerFootprintData]);

  const onPopupClosedByUser = () => {
    setSelectedProviders([]);
    providerFootprintContext.setSelectedContractEntity(null);
    if (!manualMapControl) {
      recenterMap();
    }
  };

  const handleRecenterIconClick = () => {
    setManualMapControl(false);
    recenterMap();
  };

  const loading = providerFootprintContext.providerFootprintData === undefined;

  const mapStyleOptions: MapStyle[] = [
    { name: "Light", url: mappingContext.qesLightStyle },
    { name: "Terrain", url: mappingContext.qesOutdoorsStyle },
  ];

  const providerCircleOpacity = providerFootprintContext.selectedContractEntity === null ? 0.6 : 0.3;
  const providerCircleBorderOpacity = providerFootprintContext.selectedContractEntity === null ? 1 : 0.5;

  const mapCircleColorExpression: DataDrivenPropertyValueSpecification<string> = [
    "match",
    ["get", "contractEntityColorKey"],
    CONTRACT_ENTITY_1_COLOR_SLOT_KEY, getColor(CONTRACT_ENTITY_1_COLOR_SLOT_KEY),
    CONTRACT_ENTITY_2_COLOR_SLOT_KEY, getColor(CONTRACT_ENTITY_2_COLOR_SLOT_KEY),
    CONTRACT_ENTITY_3_COLOR_SLOT_KEY, getColor(CONTRACT_ENTITY_3_COLOR_SLOT_KEY),
    CONTRACT_ENTITY_4_COLOR_SLOT_KEY, getColor(CONTRACT_ENTITY_4_COLOR_SLOT_KEY),
    CONTRACT_ENTITY_5_COLOR_SLOT_KEY, getColor(CONTRACT_ENTITY_5_COLOR_SLOT_KEY),
    getColor(CONTRACT_ENTITY_REST_COLOR_SLOT_KEY),
  ];

  const mapCircleBorderColorExpression: DataDrivenPropertyValueSpecification<string> = [
    "match",
    ["get", "contractEntityColorKey"],
    // Darkening factor of -0.4 ensures WCAG AA accessibility compliance against Light map style for Default color theme
    CONTRACT_ENTITY_1_COLOR_SLOT_KEY, adjustColorBrightness(getColor(CONTRACT_ENTITY_1_COLOR_SLOT_KEY), -0.4),
    CONTRACT_ENTITY_2_COLOR_SLOT_KEY, adjustColorBrightness(getColor(CONTRACT_ENTITY_2_COLOR_SLOT_KEY), -0.4),
    CONTRACT_ENTITY_3_COLOR_SLOT_KEY, adjustColorBrightness(getColor(CONTRACT_ENTITY_3_COLOR_SLOT_KEY), -0.4),
    CONTRACT_ENTITY_4_COLOR_SLOT_KEY, adjustColorBrightness(getColor(CONTRACT_ENTITY_4_COLOR_SLOT_KEY), -0.4),
    CONTRACT_ENTITY_5_COLOR_SLOT_KEY, adjustColorBrightness(getColor(CONTRACT_ENTITY_5_COLOR_SLOT_KEY), -0.4),
    adjustColorBrightness(getColor(CONTRACT_ENTITY_REST_COLOR_SLOT_KEY), -0.4),
  ];

  return (
    <MapboxSupportAlert>
      <div className="market-expansion-dashboard__map-container" data-testid="market-expansion-provider-footprint__map">
        <ReactMapGL.Map
          {...viewState}
          {...mappingContext.defaultMapProps}
          mapStyle={mapStyle}
          reuseMaps={true}
          onLoad={onMapLoad}
          onMove={(e) => onMapMove(e.viewState)}
          ref={mapRef}
        >
          {!loading && (
            <>
              {/* Layers are sorted from top-most to bottom-most and using beforeId to avoid mapbox console errors */}

              <Source id={HOVERED_COUNTY_SOURCE_ID} type="geojson" data={mapDataContext.hoverCountyBorderData ?? EMPTY_FEATURE_COLLECTION}>
                <Layer
                  id={HOVERED_COUNTY_BORDER_LAYER_ID}
                  type="line"
                  paint={{
                    "line-color": COUNTY_OUTLINE_COLORS.hovered,
                    "line-offset": 0.5,
                    "line-width": 4,
                  }}
                />
              </Source>

              <Source id={SELECTED_COUNTY_SOURCE_ID} type="geojson" data={mapDataContext.selectedCountyBorderData ?? EMPTY_FEATURE_COLLECTION}>
                <Layer
                  id={SELECTED_COUNTY_BORDER_LAYER_ID}
                  beforeId={HOVERED_COUNTY_BORDER_LAYER_ID}
                  type="line"
                  paint={{
                    "line-color": COUNTY_OUTLINE_COLORS.selected,
                    "line-width": 1.6,
                  }}
                />
              </Source>

              <Source id={PROVIDER_FOOTPRINT_COUNTY_SOURCE_ID} type="geojson" data={providerFootprintContext.countyOutlineFeatures}>
                <Layer
                  id={PROVIDER_FOOTPRINT_COUNTY_BORDER_LAYER_ID}
                  beforeId={SELECTED_COUNTY_BORDER_LAYER_ID}
                  type="line"
                  paint={{
                    "line-color": "grey",
                    "line-offset": 0,
                    "line-width": 1,
                  }}
                />
              </Source>

              <Source id={PROVIDER_FOOTPRINT_SELECTED_CE_SOURCE_ID} type="geojson" data={providerFootprintContext.selectedCEFootprintGeoJson}>
                <Layer
                  id={PROVIDER_FOOTPRINT_SELECTED_CE_LAYER_ID}
                  beforeId={PROVIDER_FOOTPRINT_COUNTY_BORDER_LAYER_ID}
                  type="circle"
                  paint={{
                    "circle-color": mapCircleColorExpression,
                    "circle-opacity": 1,
                    "circle-radius": ["get", "radius"],
                    "circle-stroke-color": mapCircleBorderColorExpression,
                    "circle-stroke-opacity": 1,
                    "circle-stroke-width": 1.5,
                  }}
                />
              </Source>

              <Source id={PROVIDER_FOOTPRINT_PROVIDER_SOURCE_ID} type="geojson" data={providerFootprintContext.footprintGeoJson}>
                <Layer
                  id={PROVIDER_FOOTPRINT_PROVIDER_LAYER_ID}
                  beforeId={PROVIDER_FOOTPRINT_SELECTED_CE_LAYER_ID}
                  type="circle"
                  paint={{
                    "circle-color": mapCircleColorExpression,
                    "circle-opacity": providerCircleOpacity,
                    "circle-radius": ["get", "radius"],
                    "circle-stroke-color": mapCircleBorderColorExpression,
                    "circle-stroke-opacity": providerCircleBorderOpacity,
                    "circle-stroke-width": 1.5,
                  }}
                />
              </Source>
            </>
          )}

          {selectedProviders.length ? (
            <ProviderFootprintPopup
              key="click-popup"
              keepOpen={true}
              onClose={onPopupClosedByUser}
              providerFeatures={selectedProviders}
            />
          ) : hoveredProviders.length && hoverPosition ? (
            <ProviderFootprintPopup
              key="hover-popup"
              keepOpen={false}
              popupPosition={hoverPosition}
              providerFeatures={hoveredProviders}
            />
          ) : null}

          <MapAppearanceControl
            colorThemeContext={ContractEntityColorTheme.Context}
            colorThemeFlyoutTitle="Provider Footprint Map Color Theme"
            colorThemeSelectLabel="Color Theme"
            mapRef={mapRef}
            onStyleChange={setMapStyle}
            position="bottom-right"
            selectedStyle={mapStyle}
            styleOptions={mapStyleOptions}
            testId="market-expansion-provider-footprint"
          />

          <RecenterControl
            ignoreInMapExport={true}
            imagePath={`${imagesDir}Recenter.png`}
            mapRef={mapRef}
            onClick={handleRecenterIconClick}
            position="bottom-right"
          />

          <NavigationControl data-html2canvas-ignore="true" position="bottom-right" showCompass={false} />
        </ReactMapGL.Map>
      </div>
    </MapboxSupportAlert>
  );
};

const ProviderFootprintMap: FC = (): JSX.Element => {
  const { errorLoadingSettings, loadingDefaultSettings } = useContext(MappingContext);
  if (loadingDefaultSettings) {
    return (<div className="market-expansion-dashboard__map-container" data-testid="market-expansion__footprint-map__loading">
      <LoadingSpinner />
    </div>);
  } else if (errorLoadingSettings) {
    return (<div className="market-expansion-dashboard__map-container" role="alert">An error has occurred</div>);
  };

  return <WrappedProviderFootprintMap />;
};

export default ProviderFootprintMap;
